import { cleanUsername } from '@sweetistics/app-exports/lib/utils/username';
import type { IndexProxy } from '@tanstack/db';
import { compact, uniq } from 'es-toolkit';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';

import type { UserPreviewCacheEntry, UserPreviewCacheStorageRecord } from '../user-preview-cache.types';
import {
  PROFILE_CACHE_PREFIX,
  USERNAME_CACHE_PREFIX,
  type UserPreviewCacheLookupMetadata,
} from '../user-preview-cache.types';

export const cleanUsernameSafe = (value: string | null | undefined): string => {
  const cleaned = cleanUsername(value);
  return cleaned.length > 0 ? cleaned : '';
};

export type UserPreviewCacheUsernameIndex = Pick<IndexProxy<string>, 'isReady' | 'equalityLookup'>;
export type UserPreviewCacheCollectionEntries = {
  entries: () => IterableIterator<[string, UserPreviewCacheStorageRecord]>;
};

export const isProfileCacheKey = (cacheKey: string): boolean => cacheKey.startsWith(PROFILE_CACHE_PREFIX);

export const isUsernameCacheKey = (cacheKey: string): boolean => cacheKey.startsWith(USERNAME_CACHE_PREFIX);

export const buildProfileCacheKey = (profileId: string): string => `${PROFILE_CACHE_PREFIX}${profileId}`;

export const buildUsernameCacheKey = (username: string): string => `${USERNAME_CACHE_PREFIX}${username}`;

export const normalizeUsernameKey = (source: string | null | undefined): string | undefined => {
  if (typeof source !== 'string') {
    return;
  }
  const cleaned = cleanUsernameSafe(source);
  if (cleaned.length === 0) {
    return;
  }
  return cleaned.toLowerCase();
};

export const extractProfileIdFromKey = (cacheKey: string | null | undefined): string | undefined => {
  if (!(cacheKey && isProfileCacheKey(cacheKey))) {
    return;
  }
  const trimmed = toTrimmedNonEmptyString(cacheKey.slice(PROFILE_CACHE_PREFIX.length));
  return typeof trimmed === 'string' ? trimmed : undefined;
};

export const extractUsernameFromKey = (cacheKey: string | null | undefined): string | undefined => {
  if (!(cacheKey && isUsernameCacheKey(cacheKey))) {
    return;
  }
  const trimmed = toTrimmedNonEmptyString(cacheKey.slice(USERNAME_CACHE_PREFIX.length));
  return typeof trimmed === 'string' ? trimmed : undefined;
};

export const normalizeCacheKey = (cacheKey: string | null | undefined): string | undefined => {
  if (!cacheKey) {
    return;
  }
  const trimmed = toTrimmedNonEmptyString(cacheKey);
  if (!trimmed) {
    return;
  }
  if (isProfileCacheKey(trimmed)) {
    return trimmed;
  }
  if (isUsernameCacheKey(trimmed)) {
    const usernameKey = normalizeUsernameKey(extractUsernameFromKey(trimmed));
    return usernameKey ? buildUsernameCacheKey(usernameKey) : undefined;
  }
  return trimmed;
};

export const resolveUserPreviewCacheKeys = (parameters: {
  profileId?: string | null;
  username?: string | null;
}): string[] => {
  const orderedKeys: string[] = [];
  if (parameters.profileId) {
    orderedKeys.push(buildProfileCacheKey(parameters.profileId));
  }

  const normalizedUsername = normalizeUsernameKey(parameters.username);
  if (normalizedUsername) {
    const usernameKey = buildUsernameCacheKey(normalizedUsername);
    if (!orderedKeys.includes(usernameKey)) {
      orderedKeys.push(usernameKey);
    }
  }

  return orderedKeys;
};

export const resolveUserPreviewCacheKey = (parameters: {
  profileId?: string | null;
  username?: string | null;
}): string | undefined => {
  const [first] = resolveUserPreviewCacheKeys(parameters);
  return first;
};

export const resolveUserPreviewCacheLookup = (parameters: {
  cacheKey?: string | null;
  profileId?: string | null;
  username?: string | null;
}): UserPreviewCacheLookupMetadata => {
  const normalizedCacheKey = normalizeCacheKey(parameters.cacheKey);
  const explicitProfileId = parameters.profileId && parameters.profileId.length > 0 ? parameters.profileId : undefined;
  const fallbackProfileId = extractProfileIdFromKey(normalizedCacheKey);
  const profileId = explicitProfileId ?? fallbackProfileId;
  const usernameKeyCandidate = normalizeUsernameKey(parameters.username);
  const usernameFromCacheKey =
    normalizedCacheKey && isUsernameCacheKey(normalizedCacheKey)
      ? normalizeUsernameKey(extractUsernameFromKey(normalizedCacheKey))
      : undefined;
  const usernameKey = usernameKeyCandidate ?? usernameFromCacheKey;

  const primaryKey = profileId ? buildProfileCacheKey(profileId) : normalizedCacheKey;

  const metadata: UserPreviewCacheLookupMetadata = {
    ...(normalizedCacheKey ? { cacheKey: normalizedCacheKey } : {}),
    ...(primaryKey ? { primaryKey } : {}),
    ...(usernameKey ? { usernameKey } : {}),
  };
  return metadata;
};

export const findUsernamePrimaryKey = (
  collection: UserPreviewCacheCollectionEntries,
  storeEntries: Map<string, UserPreviewCacheEntry>,
  usernameIndex: UserPreviewCacheUsernameIndex,
  usernameKey: string
): string | undefined => {
  try {
    if (usernameIndex.isReady) {
      const matches = usernameIndex.equalityLookup(usernameKey);
      const first = matches.values().next();
      if (!first.done) {
        const value = first.value;
        return typeof value === 'string' ? value : undefined;
      }
    }
  } catch {
    // Index may not be ready yet—fall back to scan.
  }

  for (const [key, record] of collection.entries()) {
    if (record.usernameKey === usernameKey) {
      return String(key);
    }
  }

  for (const [key, entry] of storeEntries.entries()) {
    if (entry.usernameKey === usernameKey) {
      return key;
    }
  }

  return;
};

export const mergeAliases = ({
  draftAliases,
  previousAliases,
  primaryCacheKey,
  profileId,
  usernameKey,
  cacheKey,
}: {
  draftAliases?: string[] | null;
  previousAliases?: string[] | null;
  primaryCacheKey: string;
  profileId?: string;
  usernameKey?: string;
  cacheKey: string;
}): string[] => {
  const aliasCandidates: string[] = [
    primaryCacheKey,
    ...(cacheKey ? [cacheKey] : []),
    ...(profileId ? [buildProfileCacheKey(profileId)] : []),
    ...(usernameKey ? [buildUsernameCacheKey(usernameKey)] : []),
    ...(Array.isArray(previousAliases) ? previousAliases : []),
    ...(Array.isArray(draftAliases) ? draftAliases : []),
  ];

  return uniq(compact(aliasCandidates));
};
